import { useState, useEffect } from 'react';
import { UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPut } from '@/lib/api';
import { TeamMemberFormModal } from './TeamMembersTab';


/**
 * Dropdown that lets a lawyer assign a Team Member (paralegal) to an application.
 * Includes a "+ Add New Team Member" option that opens the same modal used in
 * the Team Members management page; on create, the new member is auto-assigned.
 */
const TeamMemberSelect = ({ applicationId, currentAssignedId, onAssigned, canAssign = true }) => {
  const [members, setMembers] = useState([]);
  const [assignedId, setAssignedId] = useState(currentAssignedId || '');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/team-members');
      setMembers((data.team_members || []).filter(m => !m.disabled));
    } catch (e) { /* swallow — assignment dropdown shows whatever loaded */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchMembers(); }, []);
  useEffect(() => { setAssignedId(currentAssignedId || ''); }, [currentAssignedId]);

  const assign = async (id) => {
    setSaving(true);
    try {
      await apiPut(`/applications/${applicationId}/assign-team-member`, { assigned_team_member_id: id || null });
      setAssignedId(id || '');
      toast.success(id ? 'Team member assigned' : 'Team member cleared');
      onAssigned && onAssigned(id || null);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleSelectChange = (e) => {
    const value = e.target.value;
    if (value === '__add__') {
      setShowModal(true);
      // Reset the select back to current value so it doesn't visually stick on "__add__"
      e.target.value = assignedId || '';
      return;
    }
    assign(value);
  };

  const handleCreate = async (form) => {
    setSaving(true);
    try {
      const data = await apiPost('/team-members', { ...form, send_invite: true });
      toast.success('Team member invited');
      setShowModal(false);
      await fetchMembers();
      // Auto-assign the newly created member
      const newId = data.team_member?.user_id;
      if (newId) await assign(newId);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const assigned = members.find(m => m.user_id === assignedId);

  return (
    <div data-testid="team-member-select-card" className="mcl-card" style={{ marginBottom: '1rem' }}>
      <div className="mcl-card-header">
        <h3 className="mcl-card-header-title" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <UserCheck size={16} /> Assigned Team Member
        </h3>
      </div>
      <div className="mcl-card-body">
        {!canAssign ? (
          assigned ? (
            <div style={{ fontSize: '0.9rem' }}>
              <div style={{ fontWeight: 600 }}>{assigned.first_name} {assigned.last_name}</div>
              <div style={{ color: 'var(--mcl-text-muted)', fontSize: '0.82rem' }}>{assigned.email}</div>
            </div>
          ) : (
            <div style={{ fontSize: '0.85rem', color: 'var(--mcl-text-muted)' }}>No Team Member Assigned</div>
          )
        ) : (
          <>
            <select
              className="mcl-form-input"
              value={assignedId || ''}
              onChange={handleSelectChange}
              disabled={loading || saving}
              data-testid="team-member-select"
              style={{ maxWidth: '420px' }}
            >
              <option value="">— No Team Member Assigned —</option>
              {members.map(m => (
                <option key={m.user_id} value={m.user_id} data-testid={`tm-option-${m.user_id}`}>
                  {m.first_name} {m.last_name} — {m.email}{m.status === 'Invited' ? ' (Invited)' : ''}
                </option>
              ))}
              <option value="__add__" style={{ fontStyle: 'italic' }}>+ Add New Team Member…</option>
            </select>
            {assigned && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>
                Currently assigned: <strong style={{ color: 'var(--mcl-text-body)' }}>{assigned.first_name} {assigned.last_name}</strong> · {assigned.email}
              </div>
            )}
          </>
        )}
      </div>

      <TeamMemberFormModal
        open={showModal}
        initial={null}
        saving={saving}
        onClose={() => setShowModal(false)}
        onSubmit={handleCreate}
      />
    </div>
  );
};

export default TeamMemberSelect;
